import { type PropsWithChildren, useContext } from 'react';
import { useMemo } from 'react';

import { useColorRuleConfig } from '../../core/context/ColorRuleContext.js';
import { NAME_ACCESSOR } from '../constants.js';
import { useColorPalette } from '../context/ColorPaletteContext.js';
import { FormatterContext } from '../context/FormatterContext.js';
import { useNameAccessor } from '../context/NameAccessorContext.js';
import { TreeMapInternalDataContext } from '../context/TreeMapInternalDataContext.js';
import { useTreeMapRawData } from '../context/TreeMapRawDataContext.js';
import { useMinMaxConfig } from '../hooks/useMinMaxConfig.js';
import { useSetState } from '../store/selectors.js';
import { getColoredInternalCategoricalData } from '../utils/get-colored-internal-categorical-data.js';
import { getColoredInternalRangeData } from '../utils/get-colored-internal-range-data.js';
import { getSortedTree } from '../utils/get-sorted-tree.js';
import { getTreeMapColorPalette } from '../utils/get-tree-map-color-palette.js';
import { mapToInternalTree } from '../utils/map-to-internal-tree.js';

export const TreeMapInternalDataProvider = ({
  children,
}: PropsWithChildren) => {
  const colorPalette = useColorPalette();
  const setState = useSetState();
  const nameAccessor = useNameAccessor();
  const { tree, unit } = useTreeMapRawData();
  const { formatter } = useContext(FormatterContext);
  const minMaxConfig = useMinMaxConfig();
  const colorRules = useColorRuleConfig();

  const data = useMemo(
    () => getSortedTree(mapToInternalTree(tree, formatter, unit)),
    [tree, formatter, unit],
  );

  const isRangeLegend = nameAccessor === NAME_ACCESSOR.Value;

  const { rangeColorPalette, categoricalColorPalette } =
    getTreeMapColorPalette(colorPalette);

  const { data: internalData, legendColorMap } = useMemo(() => {
    if (isRangeLegend) {
      return getColoredInternalRangeData(
        data,
        rangeColorPalette,
        minMaxConfig,
        colorRules,
        unit,
      );
    }

    return getColoredInternalCategoricalData(
      data,
      categoricalColorPalette,
      nameAccessor,
      colorRules,
      unit,
    );
  }, [
    isRangeLegend,
    data,
    unit,
    categoricalColorPalette,
    nameAccessor,
    rangeColorPalette,
    minMaxConfig,
    colorRules,
  ]);

  setState((prev) => ({
    ...prev,
    colorMap: legendColorMap,
  }));

  return (
    <TreeMapInternalDataContext.Provider value={internalData}>
      {children}
    </TreeMapInternalDataContext.Provider>
  );
};

TreeMapInternalDataProvider['displayName'] = 'TreeMapInternalDataProvider';
